import { useEffect, useState } from "react";
import {
    getReadyRosterCards,
    createReadyRosterCard,
    updateReadyRosterCard,
    deleteReadyRosterCard,
} from "../../../services/api";
import { MarketplaceCardResponseDto, ReadyRosterCardDto } from "../../../types/types";
import { toast } from "../../../utils/toast";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

interface PaginatedResponse {
    content: MarketplaceCardResponseDto[];
    totalElements: number;
    totalPages: number;
    pageNumber: number;
    pageSize: number;
    first: boolean;
    last: boolean;
}

export const useReadyRoster = () => {
    const [allFighters, setAllFighters] = useState<MarketplaceCardResponseDto[]>([]);
    const [myFighters, setMyFighters] = useState<MarketplaceCardResponseDto[]>([]);
    const [activeTab, setActiveTab] = useState<"all" | "myCards">("all");

    const [searchLocation, setSearchLocation] = useState("");
    const [searchName, setSearchName] = useState("");
    const [searchSport, setSearchSport] = useState("");
    const [filterWeightClass, setFilterWeightClass] = useState("");
    const [filterRosterStatus, setFilterRosterStatus] = useState("");
    const [wins, setWins] = useState("");
    const [titleHolder, setTitleHolder] = useState<boolean | undefined>(undefined);
    const [fights, setFights] = useState<number | undefined>(undefined);

    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    const { t } = useTranslation('CreateEditReadyRoster');

    const fetchData = async (isMyCard = false) => {
        try {
            setLoading(true);

            const params: any = {
                page: currentPage,
                size: pageSize,
                name: searchName || '',
                location: searchLocation || '',
                combatSport: searchSport || '',
                weightClass: filterWeightClass || '',
                rosterStatus: filterRosterStatus || '',
                wins: wins || '',
                myCard: isMyCard
            };

            if (titleHolder !== undefined) {
                params.titleHolder = titleHolder;
            }
            if (fights !== undefined) {
                params.fights = fights;
            }

            const response: PaginatedResponse = await getReadyRosterCards(params);

            const cards = response?.content ?? [];

            if (isMyCard) {
                setMyFighters(cards);
            } else {
                setAllFighters(cards);
            }

            setTotalElements(response.totalElements);
            setTotalPages(response.totalPages);
        } catch (err) {
            console.error("Failed to fetch ready roster cards:", err);
            if (isMyCard) setMyFighters([]);
            else setAllFighters([]);
            setTotalElements(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(activeTab === "myCards");
    }, [
        activeTab,
        currentPage,
        pageSize,
        searchName,
        searchLocation,
        searchSport,
        filterWeightClass,
        filterRosterStatus,
        wins,
        titleHolder,
        fights,
    ]);

    const handleSportChange = (value: string) => {
        setSearchSport(value === "ALL" ? "" : value);
        setCurrentPage(0);
    };

    const handleWeightClassFilterChange = (value: string) => {
        setFilterWeightClass(value === "ALL" ? "" : value);
        setCurrentPage(0);
    };

    const handleRosterStatusFilterChange = (value: string) => {
        setFilterRosterStatus(value === "ALL" ? "" : value);
        setCurrentPage(0);
    };

    const handleWinsChange = (value: string) => {
        setWins(value === "ALL" ? "" : value);
        setCurrentPage(0);
    };

    const handleTitleHolderChange = (value: string) => {
        setTitleHolder(value === "ALL" ? undefined : value === "true");
        setCurrentPage(0);
    };

    const handleFightsChange = (value: string) => {
        if (value === "" || value === "ALL") setFights(undefined);
        else {
            const n = parseInt(value, 10);
            setFights(isNaN(n) ? undefined : n);
        }
        setCurrentPage(0);
    };

    const handleRefresh = () => {
        setSearchLocation("");
        setSearchName("");
        setSearchSport("");
        setFilterWeightClass("");
        setFilterRosterStatus("");
        setWins("");
        setTitleHolder(undefined);
        setFights(undefined);
        setCurrentPage(0);
    };

    const handleDelete = async (cardId: string | number) => {
        try {
            await deleteReadyRosterCard(cardId);
            toast.success(t("ready_roster_deleted_success"));
            fetchData(activeTab === "myCards");
        } catch (error: any) {
            const msg = error?.response?.data?.message;
            toast.error(msg);
        }
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(0);
    };
    const handleTabChange = (tab: "all" | "myCards") => {
        setActiveTab(tab);
        setCurrentPage(0);
    };

    const fighters = activeTab === "myCards" ? myFighters : allFighters;

    return {
        fighters,
        allFighters,
        myFighters,
        searchLocation,
        setSearchLocation,
        searchName,
        setSearchName,
        searchSport,
        setSearchSport,
        filterWeightClass,
        setFilterWeightClass,
        filterRosterStatus,
        setFilterRosterStatus,
        wins,
        setWins,
        titleHolder,
        setTitleHolder,
        fights,
        setFights,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        totalElements,
        totalPages,
        loading,
        activeTab,
        handleSportChange,
        handleWeightClassFilterChange,
        handleRosterStatusFilterChange,
        handleWinsChange,
        handleTitleHolderChange,
        handleFightsChange,
        handleRefresh,
        handleDelete,
        handlePageChange,
        handlePageSizeChange,
        handleTabChange,
        fetchData
    };
};

export const useCreateEditReadyRoster = (
    initialData?: MarketplaceCardResponseDto,
    fetchData?: (isMyCard?: boolean) => Promise<void>,
) => {
    const [purse, setPurse] = useState(initialData?.readyRosterCard?.purse || "");
    const [currency, setCurrency] = useState(initialData?.readyRosterCard?.currency || "");
    const [sport, setSport] = useState(initialData?.readyRosterCard?.sport || "");
    const [rosterStatus, setRosterStatus] = useState(initialData?.readyRosterCard?.rosterStatus || "");
    const [preferredRuleSet, setPreferredRuleSet] = useState(
        initialData?.readyRosterCard?.preferredRuleSet || "",
    );
    const [location, setLocation] = useState(initialData?.readyRosterCard?.location || "");
    const [availableRegions, setAvailableRegions] = useState<string[]>(
        initialData?.readyRosterCard?.availableRegions || [],
    );
    const [availableDays, setAvailableDays] = useState<string[]>(
        initialData?.readyRosterCard?.availableDays || [],
    );
    const [notes, setNotes] = useState(initialData?.readyRosterCard?.notes || "");
    const [weightClass, setWeightClass] = useState(initialData?.readyRosterCard?.weightClass || "");
    const [travelReady, setTravelReady] = useState(
        initialData?.readyRosterCard?.travelReady || false,
    );
    const [noticePeriodDays, setNoticePeriodDays] = useState(
        initialData?.readyRosterCard?.noticePeriodDays || 0,
    );
    const [selectedDate, setSelectedDate] = useState<any>(null);
    const [validUpto, setValidUpto] = useState<Date | null>(null);

    const { t } = useTranslation('CreateEditReadyRoster');

    useEffect(() => {
        console.log("=======>initial", initialData);

        if (!initialData) return;

        setPurse(initialData.readyRosterCard?.purse || "");
        setCurrency(initialData.readyRosterCard?.currency || "");
        setSport(initialData.readyRosterCard?.sport || "");
        setPreferredRuleSet(initialData.readyRosterCard?.preferredRuleSet || "");
        setLocation(initialData.readyRosterCard?.location || "");
        setAvailableRegions(initialData.readyRosterCard?.availableRegions || []);
        setAvailableDays(initialData.readyRosterCard?.availableDays || []);
        setNotes(initialData.readyRosterCard?.notes || "");
        setRosterStatus(initialData.readyRosterCard?.rosterStatus || "");
        setWeightClass(initialData.readyRosterCard?.weightClass || "");
        setTravelReady(initialData.readyRosterCard?.travelReady || false);
        setNoticePeriodDays(initialData.readyRosterCard?.noticePeriodDays || 0);
        setSelectedDate(null);

        if (initialData.readyRosterCard?.validUpto) {
            const validUptoDate = typeof initialData.readyRosterCard?.validUpto === 'string'
                ? dayjs(initialData.readyRosterCard?.validUpto).toDate()
                : initialData.readyRosterCard?.validUpto;
            setValidUpto(validUptoDate);
        }
    }, [initialData]);

    const resetForm = () => {
        setPurse("");
        setCurrency("");
        setSport("");
        setPreferredRuleSet("");
        setLocation("");
        setAvailableRegions([]);
        setAvailableDays([]);
        setNotes("");
        setWeightClass("");
        setRosterStatus("");
        setTravelReady(false);
        setNoticePeriodDays(0);
        setSelectedDate(null);
        setValidUpto(null);
    };

    const addAvailableDay = () => {
        if (!selectedDate) return;
        const dateString = selectedDate.toString().split("T")[0];
        if (!availableDays.includes(dateString)) {
            setAvailableDays((prev) => [...prev, dateString]);
        }
        setSelectedDate(null);
    };

    const removeAvailableDay = (date: string) =>
        setAvailableDays((prev) => prev.filter((d) => d !== date));

const handleSave = async () => {
    const payload = {
        marketplaceCardId: initialData?.id || 0,
        purse,
        currency,
        sport,
        preferredRuleSet,
        location,
        availableDays,
        notes,
        weightClass,
        rosterStatus,
        validUpto,
        travelReady,
        noticePeriodDays,
    };

    try {
        if (initialData?.id) {
            await updateReadyRosterCard(payload); 
            toast.success(t("Ready_updated_success"));
        } else {
            await createReadyRosterCard(payload);
            toast.success(t("Ready_Created_success"));
        }

        resetForm();
        if (fetchData) await fetchData(true);
        return true;
    } catch (err: any) {
        const errMsg = err?.response?.data?.message;
        if (Array.isArray(errMsg)) {
            errMsg.forEach((msg: string) => toast.error(msg));
        } else if (errMsg) {
            toast.error(errMsg);
        }
        return false;
    }
};

    return {
        purse,
        setPurse,
        validUpto,
        setValidUpto,
        currency,
        setCurrency,
        sport,
        setSport,
        preferredRuleSet,
        setPreferredRuleSet,
        location,
        setLocation,
        availableRegions,
        setAvailableRegions,
        availableDays,
        setAvailableDays,
        notes,
        setNotes,
        weightClass,
        setWeightClass,
        travelReady,
        setTravelReady,
        noticePeriodDays,
        setNoticePeriodDays,
        selectedDate,
        setSelectedDate,
        addAvailableDay,
        removeAvailableDay,
        rosterStatus,
        setRosterStatus,
        handleSave,
        resetForm,
    };
};